// TemplateManager.jsx
// -------------------------------------------------------------
// UI component for email template management
// -------------------------------------------------------------

import { useState, useRef, useEffect } from "react";
import { FiFileText, FiCheckCircle, FiInfo, FiSave, FiAlertTriangle } from "react-icons/fi";
import useSettings from "../hooks/useSettings";
import { useToast } from "../contexts/ToastContext";
import { useI18n } from "../contexts/I18nContext";

function Tooltip({ text, children }) {
  const [visible, setVisible] = useState(false);
  return (
    <div style={{ position: "relative", display: "block" }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}>
      {children}
      {visible && text && (
        <div style={{ position: "absolute", bottom: "calc(100% + 6px)", left: "50%",
          transform: "translateX(-50%)", backgroundColor: "#222", color: "#fff",
          fontSize: "11px", lineHeight: "1.5", padding: "6px 10px", borderRadius: "6px",
          whiteSpace: "pre-wrap", maxWidth: "240px", width: "max-content",
          zIndex: 9999, boxShadow: "0 2px 8px rgba(0,0,0,0.25)", pointerEvents: "none" }}>
          {text}
          <div style={{ position: "absolute", top: "100%", left: "50%",
            transform: "translateX(-50%)", border: "5px solid transparent",
            borderTopColor: "#222" }} />
        </div>
      )}
    </div>
  );
}

export default function TemplateManager() {
  const { t } = useI18n();
  const { settings, loading, saveCustomTemplate, resetCustomTemplate } = useSettings();
  const { showToast } = useToast();

  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [capturedHtml, setCapturedHtml] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => { isMountedRef.current = false; };
  }, []);

  if (!settings) return null;

  const handleCaptureTemplate = () => {
    setCapturing(true);
    setTimeout(() => {
      try {
        if (
          typeof window.Office === "undefined" ||
          typeof window.Office.context === "undefined" ||
          typeof window.Office.context.mailbox === "undefined" ||
          typeof window.Office.context.mailbox.item === "undefined"
        ) {
          showToast(t("Office.js ist nicht verfügbar. Diese Funktion ist nur in Outlook verfügbar."), "error");
          setCapturing(false);
          return;
        }

        const item = window.Office.context.mailbox.item;
        item.body.getAsync(window.Office.CoercionType.Html, function (bodyResult) {
          try {
            if (bodyResult.status !== window.Office.AsyncResultStatus.Succeeded) {
              showToast(t("Fehler beim Erfassen des E-Mail-Inhalts: {message}", { message: bodyResult.error?.message || t("Unbekannter Fehler") }), "error");
              setCapturing(false);
              return;
            }

            let htmlContent = bodyResult.value || "";
            htmlContent = htmlContent.replace(/<img[^>]*src=["']cid:[^"']*["'][^>]*>/gi, "");
            htmlContent = htmlContent.replace(/<img[^>]*src=["']data:image\/[^"']*["'][^>]*>/gi, "");

            const textContent = htmlContent.replace(/<[^>]*>/g, "").trim();
            if (!textContent || textContent.length === 0) {
              showToast(t("Der E-Mail-Inhalt ist leer. Bitte geben Sie zuerst einen Text ein."), "error");
              setCapturing(false);
              return;
            }

            setCapturedHtml(htmlContent);
            setShowSaveConfirm(true);
            setCapturing(false);
          } catch (innerErr) {
            showToast(t("Fehler beim Verarbeiten des E-Mail-Inhalts: {message}", { message: innerErr.message }), "error");
            setCapturing(false);
          }
        });
      } catch (err) {
        showToast(t("Fehler beim Erfassen der Vorlage: {message}", { message: err.message }), "error");
        setCapturing(false);
      }
    }, 100);
  };

  const handleSaveTemplate = async () => {
    if (!capturedHtml) { showToast(t("Kein E-Mail-Inhalt erfasst."), "error"); return; }
    const success = await saveCustomTemplate(capturedHtml);
    if (success) {
      setShowSaveConfirm(false);
      setCapturedHtml(null);
    }
  };

  const handleResetTemplate = async () => {
    if (showResetConfirm) {
      const success = await resetCustomTemplate();
      if (success) setShowResetConfirm(false);
    } else {
      setShowResetConfirm(true);
    }
  };

  return (
    <div style={cardStyle}>
      <h3><FiFileText size={16} style={{ marginRight: "6px", verticalAlign: "middle" }} />{t("E-Mail-Vorlage")}</h3>

      <div style={{ marginBottom: "16px" }}>
        <p style={{ fontSize: "13px", color: "#666", margin: "0 0 10px 0" }}>
          {settings.hasCustomTemplate ? (
            <span style={{ color: "#28a745", fontWeight: "500" }}>
              <FiCheckCircle size={13} style={{ marginRight: "4px", verticalAlign: "middle" }} />{t("Sie verwenden Ihre selbst erstellte Vorlage")}
            </span>
          ) : (
            <span style={{ color: "#6c757d" }}>{t("Standard-Vorlage wird verwendet")}</span>
          )}
        </p>
      </div>

      {!showSaveConfirm && (
        <div style={{ marginBottom: "16px" }}>
          <Tooltip text={t("Der erfasste E-Mail-Inhalt wird als Ihre persönliche Vorlage gespeichert und bei verschlüsselten E-Mails verwendet.")}>
            <button
              onClick={handleCaptureTemplate}
              disabled={capturing || loading || settings.passwortOptionenGuiDeaktivieren}
              style={btnCapture(capturing || loading || settings.passwortOptionenGuiDeaktivieren)}
            >
              {capturing ? t("Erfasse E-Mail-Inhalt...") : t("Als Vorlage übernehmen")}
            </button>
          </Tooltip>
          <p style={{ color: "#666", fontSize: "13px", margin: "12px 0 0 0", textAlign: "justify" }}>
            <span style={{ fontWeight: 600, color: "#555" }}><FiInfo size={12} style={{ marginRight: "4px", verticalAlign: "middle" }} />{t("Hinweis:")}</span>{" "}
            {t("Eingebettete Bilder (z. B. Logos) werden aktuell nicht übernommen und beim Versand ignoriert.")}
          </p>
        </div>
      )}

      {showSaveConfirm && (
        <div style={confirmBoxStyle}>
          <p style={{ margin: "0 0 10px 0", fontWeight: "500" }}><FiSave size={14} style={{ marginRight: "6px", verticalAlign: "middle" }} />{t("Vorlage speichern?")}</p>
          <p style={{ margin: "0 0 10px 0", fontSize: "0.9em" }}>
            {t("Der erfasste E-Mail-Inhalt wird als Ihre persönliche Vorlage gespeichert.")}
          </p>
          <div style={{ display: "flex", gap: "8px" }}>
            <button onClick={handleSaveTemplate} disabled={loading} style={btnSuccess(loading)}>
              {t("Ja, speichern")}
            </button>
            <button onClick={() => { setShowSaveConfirm(false); setCapturedHtml(null); }} disabled={loading} style={btnGray}>
              {t("Abbrechen")}
            </button>
          </div>
        </div>
      )}

      {settings.hasCustomTemplate && (
        <div style={{ marginTop: "16px" }}>
          <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
            {t("Vorlage zurücksetzen:")}
          </label>
          {showResetConfirm ? (
            <div style={confirmBoxStyle}>
              <p style={{ margin: "0 0 10px 0", fontWeight: "500" }}><FiAlertTriangle size={14} style={{ marginRight: "6px", verticalAlign: "middle" }} />{t("Sind Sie sicher?")}</p>
              <p style={{ margin: "0 0 10px 0", fontSize: "0.9em" }}>
                {t("Die benutzerdefinierte Vorlage wird gelöscht und die Standard-Vorlage wird verwendet.")}
              </p>
              <div style={{ display: "flex", gap: "8px" }}>
                <button onClick={handleResetTemplate} disabled={loading} style={{ ...btnDanger(loading), padding: "8px 16px", fontWeight: "500", fontSize: "13px" }}>
                  {t("Ja, zurücksetzen")}
                </button>
                <button onClick={() => setShowResetConfirm(false)} disabled={loading} style={{ ...btnGray, padding: "8px 16px", fontWeight: "500", fontSize: "13px" }}>
                  {t("Abbrechen")}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowResetConfirm(true)}
              disabled={loading || settings.passwortOptionenGuiDeaktivieren}
              style={{ ...btnDanger(loading || settings.passwortOptionenGuiDeaktivieren), width: "100%" }}
            >
              {t("Vorlage zurücksetzen")}
            </button>
          )}
        </div>
      )}

    </div>
  );
}

const cardStyle = {
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  border: "1px solid #e0e0e0",
  boxShadow: "0 4px 16px rgba(100,100,100,0.18)",
  padding: "16px 14px",
};

const confirmBoxStyle = {
  background: "#fff3cd",
  border: "1px solid #ffc107",
  padding: "12px",
  borderRadius: "6px",
  marginBottom: "8px",
};

const btnCapture = (disabled) => ({
  width: "100%",
  padding: "10px 24px",
  borderRadius: "6px",
  background: disabled ? "#ccc" : "#0078d7",
  color: "#fff",
  border: "none",
  cursor: disabled ? "not-allowed" : "pointer",
  fontWeight: "600",
  fontSize: "14px",
  whiteSpace: "nowrap",
});

const btnSuccess = (loading) => ({
  padding: "8px 16px",
  borderRadius: "6px",
  background: loading ? "#ccc" : "#28a745",
  color: "#fff",
  border: "none",
  cursor: loading ? "not-allowed" : "pointer",
  fontWeight: "500",
});

const btnDanger = (disabled) => ({
  padding: "10px 24px",
  borderRadius: "6px",
  background: disabled ? "#ccc" : "#dc3545",
  color: "#fff",
  border: "none",
  cursor: disabled ? "not-allowed" : "pointer",
  fontWeight: "600",
  fontSize: "14px",
  whiteSpace: "nowrap",
});

const btnGray = {
  padding: "10px 24px",
  borderRadius: "6px",
  background: "#6c757d",
  color: "#fff",
  border: "none",
  cursor: "pointer",
  fontWeight: "600",
  fontSize: "14px",
  whiteSpace: "nowrap",
};
